import React, {useState} from "react";
import {Dialog} from "@mui/material";
import {PhotoViewer, ViewerPhoto} from "./PhotoViewer";
import {DrawableImage, PHOTO_DIALOG_PAPER} from "../../pages/estimate/DrawableImage";
import {PhotoEdit} from "../services/image-service";

/** A photo the editor can open: its stored image's id (the editor saves by it). */
export interface EditableViewerPhoto extends ViewerPhoto {
  imageId?: number;
}

export interface PhotoViewerWithEditorProps<P extends EditableViewerPhoto> {
  photos: P[];
  /** The photo to open on; null keeps the viewer closed. */
  startIndex: number | null;
  onClose: () => void;
  /** Saves an edit of [photo]; absent: no Edit Picture. A photo without an imageId, or with canEdit false, is never edited. */
  onSave?: (photo: P, edit: PhotoEdit) => Promise<void>;
  onDelete?: (photo: P) => void;
  deleteText?: string;
  label?: string;
  badge?: string;
}

/**
 * The one way the record pages open a photo (SQ-657), Android's
 * FullScreenPhotoViewer with its PhotoEditorSheet: the shared viewer, and
 * Edit Picture opens the editor over it. Cancel or a save comes back to the
 * viewer on the same photo, which shows the saved edit.
 */
export function PhotoViewerWithEditor<P extends EditableViewerPhoto>(props: PhotoViewerWithEditorProps<P>) {
  const {photos, onSave, onDelete} = props;
  const [editing, setEditing] = useState<P>();
  const [saving, setSaving] = useState(false);
  const viewerPhotos = photos.map(p => (p.imageId === undefined ? {...p, canEdit: false} : p));
  const save = (edit: PhotoEdit) => {
    if (!editing || !onSave) return;
    setSaving(true);
    // A failed save leaves the editor open with the marks; the caller says why.
    onSave(editing, edit).then(() => setEditing(undefined)).catch(() => undefined).finally(() => setSaving(false));
  };
  return (
    <>
      <PhotoViewer photos={viewerPhotos} startIndex={props.startIndex} onClose={props.onClose}
                   onEdit={onSave ? index => setEditing(photos[index]) : undefined}
                   onDelete={onDelete ? index => onDelete(photos[index]) : undefined}
                   deleteText={props.deleteText} label={props.label} badge={props.badge} hidden={!!editing}/>
      <Dialog open={!!editing} onClose={() => setEditing(undefined)} fullScreen disableEscapeKeyDown PaperProps={PHOTO_DIALOG_PAPER}>
        <DrawableImage image={editing?.imageId !== undefined ? {id: editing.imageId, s3Url: editing.url} : null} readonly={false}
                       isSaving={saving} onClose={() => setEditing(undefined)} onSave={(_, edit) => save(edit)}/>
      </Dialog>
    </>
  );
}
